import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Select,
  Steps,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  LockOutlined,
  MailOutlined,
  MedicineBoxOutlined,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getPublicOrganisations, submitSignupRequest } from "../../services/authService";

const { Title, Text, Link } = Typography;
const { Option } = Select;

const BRAND_BLUE = "#1890ff";

const ROLE_OPTIONS = [
  {
    value: "PATIENT",
    label: "Patient",
    description: "Register as a patient to access medical services",
    color: "#52c41a",
  },
  {
    value: "DOCTOR",
    label: "Doctor",
    description: "Register as a doctor to manage patients and prescriptions",
    color: "#1677ff",
  },
  {
    value: "PHARMACIST",
    label: "Pharmacist",
    description: "Register as a pharmacist to manage prescriptions",
    color: "#722ed1",
  },
];

const SPECIALTY_OPTIONS = [
  "General Practice", "Internal Medicine", "Pediatrics", "Cardiology",
  "Dermatology", "Neurology", "Oncology", "Orthopedics", "Psychiatry",
  "Radiology", "Surgery", "Obstetrics & Gynecology", "Ophthalmology",
  "Urology", "Endocrinology", "Gastroenterology", "Pulmonology", "Other",
];

export default function Signup() {
  const navigate   = useNavigate();
  const [form]     = Form.useForm();
  const [step,     setStep]     = useState(0);   // 0=role, 1=personal, 2=role-specific, 3=org, 4=done
  const [role,     setRole]     = useState(null);
  const [orgs,     setOrgs]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [formData, setFormData] = useState({});

  useEffect(() => {
    getPublicOrganisations()
      .then(res => setOrgs(res.data || []))
      .catch(() => setOrgs([]));
  }, []);

  const clearError = () => { if (error) setError(""); };

  const handleRoleSelect = (selectedRole) => {
    setRole(selectedRole);
    setFormData(prev => ({ ...prev, role: selectedRole }));
    setStep(1);
  };

  const handlePersonalNext = (values) => {
    setFormData(prev => ({ ...prev, ...values }));
    setStep(2);
  };

  const handleRoleSpecificNext = (values) => {
    setFormData(prev => ({ ...prev, profile_data: values }));
    setStep(3);
  };

  const handleOrgNext = (values) => {
    setFormData(prev => ({ ...prev, ...values }));
    setStep(4);
    handleSubmit({ ...formData, ...values });
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    setError("");
    try {
      const payload = {
        email:        data.email,
        password:     data.password,
        first_name:   data.first_name,
        last_name:    data.last_name,
        phone:        data.phone || "",
        role:         data.role,
        organisation: data.organisation || null,
        notes:        data.notes || "",
        profile_data: data.profile_data || {},
      };
      await submitSignupRequest(payload);
    } catch (err) {
      const d = err?.response?.data;
      const msg = d?.email?.[0] || d?.detail || d?.non_field_errors?.[0] || "Submission failed. Please try again.";
      setError(msg);
      setStep(3); // go back to org step so they can re-submit
    } finally {
      setLoading(false);
    }
  };

  const stepLabels = ["Role", "Personal Info", "Professional Details", "Organisation"];

  return (
    <div style={{
      minHeight: "100vh", backgroundColor: "#f0f2f5",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", padding: "24px 16px",
    }}>
      <Card style={{
        width: "100%", maxWidth: step === 0 ? 560 : 480,
        borderRadius: 8,
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
        borderTop: `4px solid ${BRAND_BLUE}`,
      }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <span style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: 3, color: BRAND_BLUE, textTransform: "uppercase", marginBottom: 4 }}>
            MCHUSA
          </span>
          <Title level={4} style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "#1a1a2e" }}>
            Create Account
          </Title>
          <Text style={{ color: "#8c8c8c", fontSize: 13 }}>Join the Digital Healthcare Network</Text>
        </div>

        {/* Progress steps (hidden on role selection and done screens) */}
        {step > 0 && step < 4 && (
          <Steps
            current={step - 1}
            size="small"
            style={{ marginBottom: 28 }}
            items={stepLabels.map(l => ({ title: l }))}
          />
        )}

        {error && (
          <Alert type="error" showIcon closable message={error}
            style={{ marginBottom: 16 }} onClose={() => setError("")} />
        )}

        {/* ── Step 0: Role Selection ── */}
        {step === 0 && (
          <div>
            <Text style={{ display: "block", textAlign: "center", color: "#595959", marginBottom: 20, fontSize: 14 }}>
              What best describes your role?
            </Text>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {ROLE_OPTIONS.map(r => (
                <button
                  key={r.value}
                  onClick={() => handleRoleSelect(r.value)}
                  style={{
                    background: "#fff",
                    border: `1.5px solid #d9d9d9`,
                    borderRadius: 8,
                    padding: "14px 18px",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "border-color 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = r.color}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "#d9d9d9"}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 20,
                    background: `${r.color}18`,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <MedicineBoxOutlined style={{ fontSize: 18, color: r.color }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 15 }}>{r.label}</div>
                    <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 2 }}>{r.description}</div>
                  </div>
                </button>
              ))}
            </div>
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <Text style={{ color: "#8c8c8c", fontSize: 13 }}>Already have an account? </Text>
              <Link onClick={() => navigate("/login")} style={{ fontSize: 13 }}>Sign in</Link>
            </div>
          </div>
        )}

        {/* ── Step 1: Personal Info ── */}
        {step === 1 && (
          <Form form={form} layout="vertical" onFinish={handlePersonalNext} requiredMark={false}>
            <Form.Item name="first_name" label="First Name"
              rules={[{ required: true, message: "Required" }]}>
              <Input prefix={<UserOutlined style={{ color: "#bfbfbf" }} />} placeholder="First name" onChange={clearError} />
            </Form.Item>
            <Form.Item name="last_name" label="Last Name"
              rules={[{ required: true, message: "Required" }]}>
              <Input prefix={<UserOutlined style={{ color: "#bfbfbf" }} />} placeholder="Last name" onChange={clearError} />
            </Form.Item>
            <Form.Item name="email" label="Email Address"
              rules={[{ required: true, type: "email", message: "Enter a valid email" }]}>
              <Input prefix={<MailOutlined style={{ color: "#bfbfbf" }} />} placeholder="you@example.com" onChange={clearError} />
            </Form.Item>
            <Form.Item name="password" label="Password"
              rules={[{ required: true, min: 8, message: "At least 8 characters" }]}>
              <Input.Password prefix={<LockOutlined style={{ color: "#bfbfbf" }} />} placeholder="Min. 8 characters" />
            </Form.Item>
            <Form.Item
              name="confirm_password"
              label="Confirm Password"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) return Promise.resolve();
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <Input.Password prefix={<LockOutlined style={{ color: "#bfbfbf" }} />} placeholder="Repeat password" />
            </Form.Item>
            <Form.Item name="phone" label="Phone (optional)">
              <Input prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />} placeholder="+1 555 000 0000" />
            </Form.Item>
            <div style={{ display: "flex", gap: 10 }}>
              <Button style={{ flex: 1 }} onClick={() => setStep(0)}>Back</Button>
              <Button type="primary" htmlType="submit" style={{ flex: 2 }}>Continue</Button>
            </div>
          </Form>
        )}

        {/* ── Step 2: Role-Specific Fields ── */}
        {step === 2 && role === "DOCTOR" && (
          <Form form={form} layout="vertical" onFinish={handleRoleSpecificNext} requiredMark={false}>
            <Form.Item name="specialty" label="Specialty"
              rules={[{ required: true, message: "Required" }]}>
              <Select placeholder="Select your specialty" showSearch>
                {SPECIALTY_OPTIONS.map(s => <Option key={s} value={s}>{s}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="license_number" label="Medical License Number"
              rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="e.g. MD-12345678" />
            </Form.Item>
            <Form.Item name="years_of_experience" label="Years of Experience (optional)">
              <Input type="number" min={0} placeholder="e.g. 5" />
            </Form.Item>
            <div style={{ display: "flex", gap: 10 }}>
              <Button style={{ flex: 1 }} onClick={() => setStep(1)}>Back</Button>
              <Button type="primary" htmlType="submit" style={{ flex: 2 }}>Continue</Button>
            </div>
          </Form>
        )}

        {step === 2 && role === "PHARMACIST" && (
          <Form form={form} layout="vertical" onFinish={handleRoleSpecificNext} requiredMark={false}>
            <Form.Item name="pharmacy_name" label="Pharmacy Name"
              rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="e.g. City Central Pharmacy" />
            </Form.Item>
            <Form.Item name="license_number" label="Pharmacy License Number"
              rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="e.g. PH-98765432" />
            </Form.Item>
            <Form.Item name="address" label="Pharmacy Address (optional)">
              <Input.TextArea rows={2} placeholder="Street address, city, state" />
            </Form.Item>
            <div style={{ display: "flex", gap: 10 }}>
              <Button style={{ flex: 1 }} onClick={() => setStep(1)}>Back</Button>
              <Button type="primary" htmlType="submit" style={{ flex: 2 }}>Continue</Button>
            </div>
          </Form>
        )}

        {step === 2 && role === "PATIENT" && (
          <Form form={form} layout="vertical" onFinish={handleRoleSpecificNext} requiredMark={false}>
            <Form.Item name="date_of_birth" label="Date of Birth (optional)">
              <DatePicker style={{ width: "100%" }} placeholder="Select date of birth" />
            </Form.Item>
            <Form.Item name="address" label="Address (optional)">
              <Input.TextArea rows={2} placeholder="Street address, city, state" />
            </Form.Item>
            <div style={{ display: "flex", gap: 10 }}>
              <Button style={{ flex: 1 }} onClick={() => setStep(1)}>Back</Button>
              <Button type="primary" htmlType="submit" style={{ flex: 2 }}>Continue</Button>
            </div>
          </Form>
        )}

        {/* ── Step 3: Organisation + Notes ── */}
        {step === 3 && (
          <Form form={form} layout="vertical" onFinish={handleOrgNext} requiredMark={false}>
            <Form.Item
              name="organisation"
              label="Which organisation are you joining?"
              extra="Not sure? Leave blank — an administrator will assign you to the right one."
            >
              <Select placeholder="Select organisation (optional)" allowClear showSearch
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                options={orgs.map(o => ({ value: o.id, label: o.name }))}
              />
            </Form.Item>
            <Form.Item name="notes" label="Additional Notes (optional)">
              <Input.TextArea rows={3} placeholder="Any extra context for the administrator reviewing your request…" />
            </Form.Item>
            <div style={{ display: "flex", gap: 10 }}>
              <Button style={{ flex: 1 }} onClick={() => setStep(2)}>Back</Button>
              <Button type="primary" htmlType="submit" loading={loading} style={{ flex: 2 }}>
                Submit Request
              </Button>
            </div>
          </Form>
        )}

        {/* ── Step 4: Success ── */}
        {step === 4 && !error && (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <CheckCircleOutlined style={{ fontSize: 56, color: "#52c41a", marginBottom: 16 }} />
            <Title level={4} style={{ marginBottom: 8 }}>Request Submitted!</Title>
            <Text style={{ color: "#595959", fontSize: 14, display: "block", marginBottom: 24 }}>
              Your account request has been received. An Operations Manager will review
              and approve it shortly. You will be able to log in once approved.
            </Text>
            <Button type="primary" block onClick={() => navigate("/login")}>
              Back to Login
            </Button>
          </div>
        )}
      </Card>

      <div style={{ marginTop: 16, color: "#bfbfbf", fontSize: 11 }}>
        © {new Date().getFullYear()} MCHUSA — HIPAA-Compliant Healthcare Platform
      </div>
    </div>
  );
}
